import type { QueryClient } from '@tanstack/react-query';

export const HYDRATION_SYNC_CHANNEL = 'hydration-sync';
export const HYDRATION_SYNC_STORAGE_KEY = 'hydration-sync';

export const HYDRATION_QUERY_KEYS = [
  '/api/water-intake',
  '/api/stats/daily',
  '/api/stats/weekly',
  '/api/onesignal/tracking',
] as const;

let lastRefetchTime = 0;
const REFETCH_DEBOUNCE_MS = 2000;

/**
 * Invalidate and refetch all hydration-related queries.
 * Used after water logs, app resume, and cross-tab sync events.
 */
export async function refetchHydrationData(
  queryClient: QueryClient,
  reason: string,
  options: { skipDebounce?: boolean } = {}
): Promise<void> {
  const { skipDebounce = false } = options;

  if (!skipDebounce) {
    const now = Date.now();
    if (now - lastRefetchTime < REFETCH_DEBOUNCE_MS) {
      console.log(`⏳ Hydration sync: Skipping refetch (debounced) - ${reason}`);
      return;
    }
    lastRefetchTime = now;
  }

  console.log(`🔄 Hydration sync: Refetching all data - ${reason}`);

  try {
    await Promise.all(
      HYDRATION_QUERY_KEYS.map((key) =>
        queryClient.invalidateQueries({ queryKey: [key], exact: false })
      )
    );

    await Promise.all(
      HYDRATION_QUERY_KEYS.map((key) =>
        queryClient.refetchQueries({ queryKey: [key], type: 'active' })
      )
    );

    console.log(`✅ Hydration sync: All queries refetched - ${reason}`);
  } catch (error) {
    console.error(`❌ Hydration sync: Failed to refetch data - ${reason}:`, error);
  }
}

/**
 * Notify other tabs on the same device that hydration data changed.
 */
export function broadcastHydrationUpdate(source: string): void {
  if (typeof window === 'undefined') return;

  const payload = { source, timestamp: Date.now() };

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(HYDRATION_SYNC_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  }

  try {
    localStorage.setItem(HYDRATION_SYNC_STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(HYDRATION_SYNC_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable in private browsing
  }
}
